/**
 * Phase 2.5 — 한글 string → cp949 URL-encoded bytes.
 *
 * 네이버 리서치 URL 의 itemName 파라미터가 cp949 (= WHATWG "euc-kr" label 의
 * 실제 정의) 인코딩 요구. 11,172 한글 음절 (KS X 1001 + UHC 확장) 전부 커버.
 *
 * codepage v1.15.0 의 `dist/cpexcel.full` bundle (~461KB) 을 dynamic import 로
 * lazy load — watchlist add 호출 시에만 download. 평소 페이지에 영향 없음.
 */

type CpTable = { utils: { encode(cp: number, str: string): number[] } };

/** lazy-loaded cptable utils. 첫 호출 후 cache. */
let cachedCp: CpTable | null = null;

/**
 * codepage 의 UMD bundle 을 dynamic import.
 * vite build (rollup) 가 CJS module.exports 를 다양한 shape 으로 wrapping:
 *   1. mod = cptable                       (raw exports)
 *   2. mod = { default: cptable }          (ESM interop default)
 *   3. mod = { default: cptable, ...named } (default + named exports)
 * 셋 다 처리.
 */
async function getCp(): Promise<CpTable> {
  if (cachedCp) return cachedCp;
  // codepage 의 dist bundle 은 type 없음 — declared 안 했으므로 ts ignore.
  // @ts-expect-error - no declaration file for codepage/dist/cpexcel.full.js
  const raw = await import('codepage/dist/cpexcel.full.js');
  const mod = raw as unknown as Record<string, unknown>;

  // CJS UMD → vite/rollup ESM 변환 후 shape 가 minified 환경에서 다양:
  //   1. mod = { default: cptable }                 (interopRequireDefault)
  //   2. mod = { default: cptable, ...named }       (named + default)
  //   3. mod = cptable                              (raw)
  //   4. mod = { c: cptable }                       (rollup minified alias)
  // 안전: mod 의 모든 value (including default + named) 순회하면서
  // `.utils.encode` 가 function 인 첫 후보 사용.
  const isCpTable = (v: unknown): v is CpTable =>
    !!v &&
    typeof v === 'object' &&
    'utils' in v &&
    typeof (v as CpTable).utils?.encode === 'function';

  if (isCpTable(mod)) {
    cachedCp = mod;
    return cachedCp;
  }
  for (const value of Object.values(mod)) {
    if (isCpTable(value)) {
      cachedCp = value;
      return cachedCp;
    }
  }
  throw new Error(
    `codepage import shape unexpected. keys: ${Object.keys(mod).join(',')}`,
  );
}

/**
 * 한글 string ("삼성전자") → cp949 URL-encoded bytes ("%BB%EF%BC%BA%C0%FC%C0%DA").
 * 네이버 listing URL 의 itemName 에 직접 사용 가능한 형식.
 */
export async function encodeCp949Url(text: string): Promise<string> {
  const cp = await getCp();
  const bytes = cp.utils.encode(949, text);
  if (!Array.isArray(bytes)) {
    throw new Error(`encode return non-array: ${typeof bytes}`);
  }
  return bytes
    .map((b: number) => '%' + b.toString(16).padStart(2, '0').toUpperCase())
    .join('');
}
